import React, { useState, useCallback, useRef } from 'react'
import { ConfigProvider, theme as antdTheme } from 'antd'
import { streamChat } from '../../api/chatbotApiService/chatbotApiUtils'
import ChatMessages from '../../components/chatbotComponents/chatMessages'
import ChatInput from '../../components/chatbotComponents/chatInput'
import KolumbusLogo from '../../assets/images/Kolumbus-Logo.png'
import './chatbot.scss'

export default function KolumbusAIChatbot() {
  const [messages, setMessages] = useState([
    { id: 1, role: 'bot', text: 'Welcome to Kolumbus.ai! How can I help you today?' }
  ])
  const [text, setText] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const streamRef = useRef(null)

  const handleSend = useCallback((inputText) => {
    if (isStreaming) return
    if (!inputText || !inputText.trim()) return

    const id = Date.now()
    const userMsg = { id, role: 'user', text: inputText.trim() }
    const botId = id + 1
    const botMsg = {
      id: botId,
      role: 'bot',
      text: '',
      typing: true,
      thinking: '',
      inThinking: true,
      thinkingDone: false
    }
    setMessages((prev) => [...prev, userMsg, botMsg])

    // Cancel any previous stream
    if (streamRef.current) {
      try {
        streamRef.current.cancel()
      } catch {
        // Silently handle cancellation errors
      }
    }

    setIsStreaming(true)

    // Streaming parse state
    const parseState = {
      buffer: '',
      inThinking: false,
      thinking: '',
      answerFull: '',
      answerRevealed: '',
      thinkingDone: false,
      revealQueue: [],
      revealTimer: null
    }

    const appendToken = (prev, chunk) => {
      if (!chunk) return prev || ''
      if (!prev) return chunk
      return prev + chunk
    }

    const processChunk = (rawChunk) => {
      parseState.buffer += rawChunk
      let addedAnswer = ''

      while (parseState.buffer.length) {
        if (!parseState.inThinking) {
          const startIdx = parseState.buffer.indexOf('<think>')
          if (startIdx === -1) {
            const before = parseState.answerFull.length
            parseState.answerFull = appendToken(parseState.answerFull, parseState.buffer)
            addedAnswer += parseState.answerFull.slice(before)
            parseState.buffer = ''
            break
          } else {
            if (startIdx > 0) {
              const segment = parseState.buffer.slice(0, startIdx)
              const before = parseState.answerFull.length
              parseState.answerFull = appendToken(parseState.answerFull, segment)
              addedAnswer += parseState.answerFull.slice(before)
            }
            parseState.buffer = parseState.buffer.slice(startIdx + '<think>'.length)
            parseState.inThinking = true
          }
        } else {
          const endIdx = parseState.buffer.indexOf('</think>')
          if (endIdx === -1) {
            parseState.thinking = appendToken(parseState.thinking, parseState.buffer)
            parseState.buffer = ''
            break
          } else {
            if (endIdx > 0) {
              parseState.thinking = appendToken(parseState.thinking, parseState.buffer.slice(0, endIdx))
            }
            parseState.buffer = parseState.buffer.slice(endIdx + '</think>'.length)
            parseState.inThinking = false
            parseState.thinkingDone = true
          }
        }
      }

      if (!parseState.inThinking && !parseState.thinkingDone && parseState.thinking === '' && parseState.answerFull.length > 0) {
        parseState.thinkingDone = true
      }

      return { addedAnswer }
    }

    // Tokenize into words/tags preserving spaces
    const tokenRegex = /(<[^>]+>|\S+\s*)/g
    const enqueueReveal = (text) => {
      if (!text) return
      const parts = text.match(tokenRegex) || []
      parseState.revealQueue.push(...parts)
      if (!parseState.revealTimer) startRevealTimer()
    }

    const startRevealTimer = () => {
      const tick = () => {
        if (!parseState.revealQueue.length) {
          parseState.revealTimer = null
          return
        }
        const next = parseState.revealQueue.shift()
        parseState.answerRevealed += next
        setMessages(prev => prev.map(m => m.id === botId ? {
          ...m,
          text: parseState.answerRevealed,
          thinking: parseState.thinking,
          inThinking: parseState.inThinking,
          thinkingDone: parseState.thinkingDone,
          typing: parseState.inThinking || (!parseState.thinkingDone && parseState.answerRevealed.length === 0)
        } : m))
        parseState.revealTimer = requestAnimationFrame(tick)
      }
      parseState.revealTimer = requestAnimationFrame(tick)
    }

    // Helper: unwrap JSON content
    const unwrapChunk = (raw, isFirst) => {
      if (!raw) return ''
      const lines = raw.split(/\n/)
      const out = []
      for (let line of lines) {
        if (!line) continue
        const trimmed = line.trim()
        if (trimmed.startsWith('{') && trimmed.endsWith('}') && trimmed.includes('"content"')) {
          try {
            const obj = JSON.parse(trimmed)
            if (typeof obj.content === 'string') {
              let c = obj.content
              if (isFirst) c = c.replace(/^\n+/, '')
              out.push(c)
              continue
            }
          } catch {
            // Handle JSON parsing errors silently
          }
        }
        let l = line
        if (isFirst && out.length === 0) l = l.replace(/^\n+/, '')
        out.push(l)
      }
      return out.join('')
    }

    // Start streaming
    streamRef.current = streamChat(inputText.trim(), {
      onToken: (chunk) => {
        const isFirst = !parseState.answerFull && !parseState.thinking && !parseState.buffer
        const unwrapped = unwrapChunk(chunk, isFirst)
        const { addedAnswer } = processChunk(unwrapped)

        if (addedAnswer) {
          enqueueReveal(addedAnswer)
        } else {
          setMessages(prev => prev.map(m => m.id === botId ? {
            ...m,
            text: parseState.answerRevealed,
            thinking: parseState.thinking,
            inThinking: parseState.inThinking,
            thinkingDone: parseState.thinkingDone,
            typing: true
          } : m))
        }
      },
      onDone: () => {
        if (parseState.buffer) processChunk('')

        if (parseState.revealQueue.length) {
          parseState.answerRevealed += parseState.revealQueue.join('')
          parseState.revealQueue = []
        }
        if (parseState.revealTimer) {
          cancelAnimationFrame(parseState.revealTimer)
          parseState.revealTimer = null
        }
        parseState.answerRevealed = parseState.answerFull

        setMessages((prev) => prev.map(m => m.id === botId ? {
          ...m,
          text: parseState.answerRevealed,
          thinking: parseState.thinking,
          inThinking: false,
          typing: false,
          thinkingDone: parseState.thinkingDone || true
        } : m))

        streamRef.current = null
        setIsStreaming(false)
      },
      onError: (err) => {
        console.error('Chat error:', err)
        setMessages((prev) => prev.map(m => m.id === botId ? {
          ...m,
          typing: false,
          text: m.text || 'Sorry, something went wrong. Please try again.'
        } : m))
        streamRef.current = null
        setIsStreaming(false)
      }
    })
  }, [isStreaming])

  const handleStop = useCallback(() => {
    if (!isStreaming) return

    if (streamRef.current) {
      try {
        streamRef.current.cancel()
      } catch {
        // Handle cancellation errors silently
      }
      streamRef.current = null
    }

    setIsStreaming(false)

    setMessages(prev => {
      const copy = [...prev]
      for (let i = copy.length - 1; i >= 0; i--) {
        const m = copy[i]
        if (m.role === 'bot' && (m.typing || m.inThinking)) {
          copy[i] = { ...m, typing: false, inThinking: false, thinkingDone: true }
          break
        }
      }
      return copy
    })
  }, [isStreaming])

  return (
    <ConfigProvider theme={{ algorithm: antdTheme.defaultAlgorithm }}>
      <div style={{
        height: '85vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#f8fafc',
        overflow: 'hidden',
        minHeight: 0
      }}>
        {/* Header */}
        <div style={{
          background: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          padding: '16px 0px',
          display: 'flex',
          flexShrink: 0,
          alignItems: 'center'
        }}>
          <img
            src={KolumbusLogo}
            alt="Kolumbus AI"
            style={{
              height: '32px',
              width: 'auto'
            }}
          />
        </div>

        <ChatMessages messages={messages} />

        <ChatInput
          text={text}
          setText={setText}
          isStreaming={isStreaming}
          onSubmit={handleSend}
          onStop={handleStop}
        />
      </div>
    </ConfigProvider>
  )
}
